import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import LanguageSelectionView from './LanguageSelectionView';
import AccessibilitySettingsEnrollmentView from './AccessibilitySettingsEnrollmentView';
import AboutThisStudyView from './AboutThisStudyView';
import ExpectationsAndRequirements from './ExpectationsAndRequirements';
import EligibilityCriteriaLanguagesView from './EligibilityCriteriaLanguagesView';
import EligibilityCriteriaConditionsView from './EligibilityCriteriaConditionsView';
import EligibleStudiesView from './EligibleStudiesView';
import HowDidYouLearnAboutThisStudyView from './HowDidYouLearnAboutThisStudyView';
import ContactInformationView from './ContactInformationView';
import ReviewAndEnrollView from './ReviewAndEnrollView';
import NotInterestedThankYouView from './NotInterestedThankYouView';
import EnrollmentDeclinedView from './EnrollmentDeclinedView';
import EnrollmentCompletedView from './EnrollmentCompletedView';

const pages = [
  'language-selection-view',
  'accessibility-settings-enrollment-view',
  'about-this-study-view',
  'expectations-and-requirements',
  'eligibility-criteria-languages-view',
  'eligibility-criteria-conditions-view',
  'eligible-studies-view',
  'how-did-you-learn-about-this-study-view',
  'contact-information-view',
  'review-and-enroll-view',
  'not-interested-thank-you-view',
  'enrollment-declined-view',
  'enrollment-completed-view'
];

const initialEnrollment = {
  researcherEmail: '',
  startedAt: new Date()
};

const Enrollment = () => {
  const initialPage = 'language-selection-view';
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [_pageHistory, setPageHistory] = useState([initialPage]);
  const [enrollment, setEnrollment] = useState<any>({ ...initialEnrollment });
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation('enrollment');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const site = searchParams.get('site') || '';
    const language = searchParams.get('language') || '';
    if (site) {
      setEnrollment((prev: any) => ({
        ...prev,
        site,
      }));
    }
    if (language) {
      setEnrollment((prev: any) => ({
        ...prev,
        language: language,
      }));
    }
  }, []);

  useEffect(() => {
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = (_event: PopStateEvent) => {
      setShowDialog(true);
      window.history.pushState(null, document.title, window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [currentPage]);

  const handleDialogClose = () => {
    setShowDialog(false);
    window.history.pushState(null, document.title, window.location.href);
};

  const pageChange = (page: string) => {
    if (page === 'finish') {
      navigate('/');
      return;
    }
    if (page === 'back') {
      setPageHistory(prev => {
        const newHistory = [...prev];
        newHistory.pop();
        setCurrentPage(newHistory.length > 0 ? newHistory[newHistory.length - 1] : initialPage);
        return newHistory;
      });
      return;
    }
    if (!page || !pages.includes(page)) return;
    if (currentPage !== page) {
      setPageHistory(prev => [...prev, page]);
      setCurrentPage(page);
    }
  };

  // Render the correct step based on currentPage
  const renderStep = () => {
    const stepProps = { enrollment, onPageChange: pageChange };
    switch (currentPage) {
      case 'language-selection-view': return <LanguageSelectionView {...stepProps} />;
      case 'accessibility-settings-enrollment-view': return <AccessibilitySettingsEnrollmentView {...stepProps} />;
      case 'about-this-study-view': return <AboutThisStudyView {...stepProps} />;
      case 'expectations-and-requirements': return <ExpectationsAndRequirements {...stepProps} />;
      case 'eligibility-criteria-languages-view': return <EligibilityCriteriaLanguagesView {...stepProps} />;
      case 'eligibility-criteria-conditions-view': return <EligibilityCriteriaConditionsView {...stepProps} />;
      case 'eligible-studies-view': return <EligibleStudiesView {...stepProps} />;
      case 'how-did-you-learn-about-this-study-view': return <HowDidYouLearnAboutThisStudyView {...stepProps} />;
      case 'contact-information-view': return <ContactInformationView {...stepProps} />;
      case 'review-and-enroll-view': return <ReviewAndEnrollView {...stepProps} />;
      case 'not-interested-thank-you-view': return <NotInterestedThankYouView {...stepProps} />;
      case 'enrollment-declined-view': return <EnrollmentDeclinedView {...stepProps} />;
      case 'enrollment-completed-view': return <EnrollmentCompletedView />;
      default:
        return (<></>);
    }
  };

  return (
    <div>
      {renderStep()}
      <Dialog open={showDialog} onClose={handleDialogClose}>
        <DialogTitle>{t("Warning")}</DialogTitle>
        <DialogContent>
          {t("You cannot use the browser back button during registration. Please complete the registration process before leaving this page.")}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleDialogClose} color="primary" autoFocus>
            {t("Close")}
          </Button>
        </DialogActions>
    </Dialog>
    </div>
  );
};

export default Enrollment;
